import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AuthAwareLink from '../components/AuthAwareLink';
import Seo from '../seo/Seo';
import { PAGE_SEO } from '../seo/pageSeoConfig';
import { transformations } from '../data/transformations';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import { buildWebPageSchema, buildItemListSchema } from '../seo/structuredData';
import CollapsibleSeoContent from '../components/seo/CollapsibleSeoContent';
import { getSeoContent, getSeoFaqSchema } from '../seo/seoContentData';

const transformationsSeo = PAGE_SEO['/real-transformations'];

const roomDesignStyles = [
  { name: 'Modern', description: 'Clean lines, neutral tones, and practical furniture.' },
  { name: 'Luxury', description: 'Premium finishes, elegant details, and high-end styling.' },
  { name: 'Scandinavian', description: 'Light wood, white walls, and cozy textures.' },
  { name: 'Boho', description: 'Layered textures, natural materials, and relaxed accents.' },
  { name: 'Minimal', description: 'Simple layouts, calm colors, and reduced clutter.' },
  { name: 'Industrial', description: 'Dark metal, exposed materials, and warm accents.' },
  { name: 'Farmhouse', description: 'Rustic textures, cozy finishes, and practical comfort.' },
  { name: 'Classic', description: 'Timeless furniture, balanced details, and refined colors.' },
  { name: 'Japandi', description: 'Warm minimalism, soft neutrals, and natural wood.' },
  { name: 'Coastal', description: 'Natural textures, airy layouts, and relaxed colors.' },
];

const styleTitlePillClasses = [
  'rt-style-title-pill--completed',
  'rt-style-title-pill--room',
  'rt-style-title-pill--style',
  'rt-style-title-pill--created',
];

const shuffleItems = (items) => [...items].sort(() => Math.random() - 0.5);

function RealTransformationsPage() {
  const randomizedTransformations = useMemo(() => shuffleItems(transformations), []);
  const randomizedRoomDesignStyles = useMemo(() => shuffleItems(roomDesignStyles), []);

  return (
    <div className="page landing-bg rt-page">
      <Seo
        title={transformationsSeo.title}
        description={transformationsSeo.description}
        canonicalPath="/real-transformations"
        keywords="AI room transformations, before after interior design, real room redesign, AI room makeover examples"
        structuredData={[
          buildWebPageSchema({
            name: transformationsSeo.h1,
            description: transformationsSeo.description,
            path: '/real-transformations',
          }),
          buildItemListSchema(randomizedTransformations),
          getSeoFaqSchema('/real-transformations'),
        ].filter(Boolean)}
      />
      <Navbar />

      <section className="container section rt-hero">
        <h1>
          Real Room{' '}
          <span className="shop-furniture-showcase__title-accent shop-furniture-showcase__title-accent--gradient">
            Transformations
          </span>
        </h1>
        <p className="rt-hero-subtitle">
          See how MyRoomsAI turns real uploaded rooms into beautifully redesigned spaces while keeping the original room layout.
        </p>
        <Link to="/ai-tools/ai-room-decoration" className="btn btn-primary rt-hero-cta">
          Start Your Room Decoration
        </Link>
      </section>

      <section className="container section rt-gallery">
        {randomizedTransformations.map((item) => (
          <article key={item.id} className="card rt-card">
            <BeforeAfterSlider before={item.beforeImage} after={item.afterImage} />
          </article>
        ))}
      </section>

      <section className="container section rt-styles">
        <div className="rt-section-heading">
          <h2>Popular AI Room Design Styles</h2>
          <p>
            Explore realistic room transformations across different interior styles — from clean modern spaces to warm
            luxury interiors.
          </p>
        </div>
        <div className="rt-style-grid">
          {randomizedRoomDesignStyles.map((style, index) => (
            <article key={style.name} className="rt-style-card">
              <h3 className={`rt-style-title-pill ${styleTitlePillClasses[index % styleTitlePillClasses.length]}`}>
                {style.name}
              </h3>
              <p>{style.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="container section rt-cta-section">
        <div className="rt-cta-card">
          <h2>Ready to Redesign Your Own Room?</h2>
          <p>
            Upload a photo of your room and preview a new interior design before buying furniture, changing colors, or
            starting renovation work.
          </p>
          <div className="rt-cta-actions">
            <Link to="/ai-tools/ai-room-decoration" className="btn btn-primary">Start Your Room Decoration</Link>
            <a href="/gallery/" className="btn btn-ghost">View Design Gallery</a>
            <AuthAwareLink spaPath="/faq" staticPath="/faq/" className="btn btn-ghost">Read FAQ</AuthAwareLink>
          </div>
        </div>
      </section>

      <CollapsibleSeoContent {...getSeoContent('/real-transformations')} />

      <Footer />
    </div>
  );
}

export default RealTransformationsPage;
